import { useState, type FormEvent } from 'react';
import { useAuthStore } from '../stores/authStore';
import { login as apiLogin } from '../api/client';
import { BrandMark, BrandWordmark } from './BrandLogo';

interface LoginPageProps {
  onSuccess?: () => void;
  onGoToRegister?: () => void;
  embedded?: boolean;
}

/** Shared input class — matches RegisterPage exactly. No mono overrides. */
const inputClass =
  'w-full px-3.5 py-2.5 bg-gray-900/60 border border-gray-700 rounded-lg ' +
  'font-sans text-sm text-white placeholder:text-gray-500 ' +
  'transition-colors duration-150 ' +
  'hover:border-gray-600 ' +
  'focus:outline-none focus:ring-2 focus:ring-primary-500/70 focus:border-primary-500/40 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const labelClass =
  'block text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1.5';

export function LoginPage({ onSuccess, onGoToRegister, embedded }: LoginPageProps = {}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const authLogin = useAuthStore((s) => s.login);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await apiLogin(username, password);
      authLogin(result.token, result.user);
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={embedded ? undefined : 'min-h-screen app-bg flex items-center justify-center p-4'}>
      <div className="glass-panel hairline-top rounded-2xl px-8 py-10 w-full max-w-sm reveal reveal-1">
        {/* Brand header */}
        <div className="flex flex-col items-center text-center mb-8">
          <BrandMark className="w-12 h-12" />
          <BrandWordmark className="mt-4 text-2xl" />
          <p className="mt-2 text-sm text-gray-400">Sign in to your quant strategy lab</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="login-username" className={labelClass}>
              Username
            </label>
            <input
              id="login-username"
              type="text"
              name="username"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className={inputClass}
              placeholder="Enter username"
              autoFocus
              required
              disabled={loading}
            />
          </div>

          <div>
            <label htmlFor="login-password" className={labelClass}>
              Password
            </label>
            <input
              id="login-password"
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClass}
              placeholder="Enter password"
              required
              disabled={loading}
            />
          </div>

          {error && (
            <div
              role="alert"
              className="flex items-start gap-2 text-sm text-red-300 bg-red-900/20 border border-red-800/50 rounded-lg px-3.5 py-2.5"
            >
              <svg
                className="w-4 h-4 mt-0.5 shrink-0 text-red-400"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                <path d="M8 5v3.5M8 11h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn-glow w-full py-2.5 px-4 text-white font-semibold text-sm rounded-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg
                  className="animate-spin w-4 h-4 text-white/80"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Signing in…
              </span>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        {onGoToRegister && (
          <div className="mt-6 text-center text-sm text-gray-400">
            Have an invite code?{' '}
            <button
              type="button"
              onClick={onGoToRegister}
              className="font-medium text-primary-400 hover:text-primary-300 transition-colors"
            >
              Register here
            </button>
          </div>
        )}

        {/* SHA-24: legal pages must be reachable while logged out */}
        <div className="mt-8 pt-4 border-t border-gray-800 flex items-center justify-center gap-4 text-xs text-gray-500">
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gray-300 underline underline-offset-2 transition-colors"
          >
            Terms of Service
          </a>
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gray-300 underline underline-offset-2 transition-colors"
          >
            Privacy Policy
          </a>
        </div>
      </div>
    </div>
  );
}
